// The saved property strategy report, /property-strategy/report (Stage 2, docs/property-strategy-spec.md S2.8): the
// same fixed address for everyone, email 1's link. Logged in only (logged out, App.jsx leaves it to the Library's
// door, and logging in lands on the Library, decision 29). It reads the person's latest saved result under row level
// security (their own, or the viewed member's while staff view as them) and mounts the tool in saved mode: the report
// worked out again in this browser from the stored answers, history and contexts, with Download PDF and Save a copy,
// and nothing written to sessionStorage. The tool's own look fills the page, with the View as bar on top when there.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { setTitle, SITE } from '../config/site.js'
import { navigate } from '../lib/router.jsx'
import { mount } from '../strategy/app.js'
import { upgradeSession } from '../strategy/session.mjs'
import { REPORT_TITLE, STRATEGY_PATH } from '../strategy/route.js'
import { loadSavedStrategy } from '../free/load.js'
import { ASSETS, strategyHooks } from './PropertyStrategy.jsx'
import core from '../strategy/core.json'

// The page's own words (Adam's to confirm, S2.13 point 14)
export const SAVED_WORDS = {
  none: "You haven't saved a property strategy report yet.",
  old: "This report was made with an earlier version of the tool and can't be shown. Take the assessment again to make a new one.",
  button: 'Find your strategy',
}

const IN = { current: { status: 'in' } }

// The hub's own "That didn't load" notice, as App.jsx draws it
function Notice({ title, children }) {
  return (
    <main className="notice">
      <p className="notice-mark" aria-hidden="true" />
      <h1>{title}</h1>
      <p>{children}</p>
    </main>
  )
}

function SavedTool({ row }) {
  const ref = useRef(null)
  useEffect(() => mount(ref.current, {
    core, assets: ASSETS, saved: row,
    hooks: strategyHooks(IN, { home: () => navigate('/'), newRun: () => navigate(STRATEGY_PATH) }),
  }), [row])
  return <div className="pst" ref={ref} />
}

function Empty({ line }) {
  return (
    <div className="pst">
      <main className="wrap narrow strategy-saved-page">
        <p className="eyebrow">Your personal plan</p>
        <h1>{REPORT_TITLE}</h1>
        <p>{line}</p>
        <a className="btn" href={STRATEGY_PATH} onClick={e => { e.preventDefault(); navigate(STRATEGY_PATH) }}>
          {SAVED_WORDS.button} <span aria-hidden="true">→</span>
        </a>
      </main>
    </div>
  )
}

export default function StrategyReport() {
  const { me } = useMember()
  const personId = me.person.id
  const [state, setState] = useState({ status: 'loading', row: null })
  const load = useCallback(() => {
    setState({ status: 'loading', row: null })
    loadSavedStrategy(personId)
      .then(row => setState({ status: 'ready', row }))
      .catch(() => setState({ status: 'error', row: null }))
  }, [personId])
  useEffect(() => { setTitle(REPORT_TITLE); load() }, [load])

  if (state.status === 'loading') return <div className="boot" />
  if (state.status === 'error') {
    return (
      <Notice title="That didn't load">
        Check your connection and try again. If it keeps happening, email {SITE.helpEmail}.
        <span className="notice-actions"><button type="button" className="btn btn-ink" onClick={load}>Try again</button></span>
      </Notice>
    )
  }
  if (!state.row) return <Empty line={SAVED_WORDS.none} />
  if (!upgradeSession({ ...state.row, revision: 0, createdAt: Date.now() })) return <Empty line={SAVED_WORDS.old} />
  return <SavedTool row={state.row} />
}
